import React, { useState, useEffect } from 'react';
import { studentAPI } from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';

const AddStudent = () => {
  const { isSidebarOpen = true } = useSidebar() || {};
  const sidebarWidth = isSidebarOpen ? '17.6rem' : '3.4rem';

  const [studentData, setStudentData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    admissionNo: '',
    collegeName: '',
    accountType: 'adminCreated',
  });

  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedFile, setSelectedFile] = useState('No file chosen');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStudentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!Object.values(studentData).every(Boolean)) {
      setMessage('Please fill all fields');
      setIsError(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...studentData,
        accountType: 'adminCreated',
      };

      console.log('Submitting student data:', dataToSend);

      await studentAPI.register(dataToSend);
      setMessage('Student added successfully');
      setIsError(false);
      setStudentData({
        username: '',
        email: '',
        password: '',
        fullName: '',
        admissionNo: '',
        collegeName: '',
        accountType: 'adminCreated',
      });
      setShowModal(false);
    } catch (error) {
      console.error('Registration error:', error);
      setMessage(
        error.response?.status === 400
          ? 'Student with this email, username, or admission number already exists.'
          : error.response?.data?.message ||
            (error.response?.status === 404 ? 'Student registration endpoint not found' : 'Error adding student')
      );
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile('No file chosen');
      return;
    }

    setSelectedFile(file.name);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setLoading(true);
        const rows = event.target.result.split('\n').filter((row) => row.trim());
        if (rows.length < 1) throw new Error('CSV file is empty');

        const expectedHeaders = 'username,email,password,fullName,admissionNo,collegeName';
        if (rows[0].trim() !== expectedHeaders) throw new Error(`Invalid CSV headers. Expected: ${expectedHeaders}`);

        const students = rows.slice(1).filter((row) => row.trim()).map((row) => {
          const fields = row.split(',').map((field) => field.trim());
          if (fields.length !== 6) throw new Error(`Invalid row format: ${row}`);
          return {
            username: fields[0] || '',
            email: fields[1] || '',
            password: fields[2] || '',
            fullName: fields[3] || '',
            admissionNo: fields[4] || '',
            collegeName: fields[5] || '',
            accountType: 'adminCreated',
          };
        });

        if (students.length === 0) throw new Error('No valid students found in CSV');
        if (students.some((student) => !Object.values(student).every(Boolean))) {
          throw new Error('CSV contains rows with missing required fields');
        }

        const response = await studentAPI.createBulk({ students });
        setMessage(response.data.message || `Successfully added ${students.length} students`);
        setIsError(false);
      } catch (error) {
        console.error('Bulk upload error:', error);
        setMessage(
          error.response?.status === 400
            ? 'Bulk upload endpoint not found. Please check the server configuration.'
            : error.response?.data?.message || error.message || 'Error uploading students'
        );
        setIsError(true);
      } finally {
        setLoading(false);
        e.target.value = null;
        setSelectedFile('No file chosen');
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const csvContent = [
      'username,email,password,fullName,admissionNo,collegeName',
      'johndoe,john.doe@example.com,Pass1234,John Doe,ADM001,Example College',
      'janedoe,jane.doe@example.com,Pass5678,Jane Doe,ADM002,Example College',
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'student_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setStudentData({
      username: '',
      email: '',
      password: '',
      fullName: '',
      admissionNo: '',
      collegeName: '',
      accountType: 'adminCreated',
    });
    setMessage('');
    setIsError(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setMessage('');
    setIsError(false);
  };

  // Updated Inline Styles
  const pageStyle = {
    minHeight: '100vh',
    height: '100vh',
    width: '100vw',
    background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Roboto', sans-serif",
    margin: 0,
    padding: 0,
    position: 'relative',
    overflow: 'hidden',
  };
  const mainContentStyle = {
    padding: '2rem 3rem', // Increased padding for more space around content
    margin: '5rem',
    backgroundColor: 'white',
    width: isMobile ? '90%' : `calc((100vw - ${sidebarWidth}) * 0.75 - 2rem)`,
    maxWidth: isMobile ? '50%' : 'none',
    minHeight: '400px', // Increased to accommodate content without scrolling
    maxHeight: '400px', // Increased to match minHeight
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '0.75rem',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    // Removed overflow: 'auto' to disable scrolling
    ':hover': {
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },
  };
  const cardHeaderContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem', // Increased for better spacing with larger padding
    width: '100%',
  };
  const cardHeaderStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1F2937',
  };
  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #D1D5DB',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    transition: 'border-color 0.3s ease',
  };
  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.3rem',
    display: 'block',
  };
  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 1.2rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#6B21A8',
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
    cursor: 'pointer',
    borderRadius: '1.5rem',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    transition: 'all 0.4s ease',
  };
  const alertStyle = {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem', // Increased for better spacing
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: isError ? '#FEE2E2' : '#DCFCE7',
    color: isError ? '#B91C1C' : '#166534',
    width: '100%',
    fontSize: '0.875rem',
  };
  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: isError ? '#B91C1C' : '#166534',
  };
  const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  };
  const fileInputStyle = {
    display: 'none',
  };
  const fileInputLabelStyle = {
    ...buttonStyle,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1,
    width: '100%',
    maxWidth: '150px',
  };
  const fileInputContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '1rem', // Increased for better spacing
    width: '100%',
  };
  const fileNameBoxStyle = {
    border: '1px solid #D1D5DB',
    borderRadius: '0.375rem',
    padding: '3rem',
    fontSize: '0.875rem',
    color: '#374151',
    backgroundColor: '#F9FAFB',
    width: '100%',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    marginTop: '0.3rem',
  };
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  };
  const modalStyle = {
    background: 'linear-gradient(to right, #FFE3E3, #D6E6FF, #EBD6FF)',
    borderRadius: '0.75rem',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
    padding: '1rem',
    width: '100%',
    maxWidth: '500px',
  };
  const modalHeaderStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: '#1F2937',
  };
  const modalButtonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '0.5rem',
  };
  const modalCancelButtonStyle = {
    backgroundColor: '#D1D5DB',
    color: '#374151',
    padding: '0.3rem 0.8rem',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    minWidth: '120px',
    height: '30px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  };
  const contentContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    flex: 1,
    justifyContent: 'space-between',
  };
  const noteStyle = {
    fontSize: '0.875rem',
    color: '#6B7280',
    marginBottom: '1rem', // Increased for better spacing
    textAlign: 'center',
    width: '100%',
  };

  return (
    <>
      <style>
        {`
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            width: 100%;
            overflow: hidden;
          }
          @keyframes glow {
            0% { box-shadow: 0 0 12px rgba(100, 150, 255, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
            50% { box-shadow: 0 0 18px rgba(100, 150, 255, 1), 0 0 25px rgba(180, 100, 255, 0.9); }
            100% { box-shadow: 0 0 12px rgba(100, 150, 255, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
          }
          .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .modal-cancel-button:hover:not(:disabled) {
            background-color: #B0BEC5;
          }
          .input-field:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
        `}
      </style>
      <div style={pageStyle}>
        <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', zIndex: '20', background: 'transparent' }}>
          <AdminNavbar />
        </div>
        <div style={{ display: 'flex', flex: '1', height: '100vh', width: '100vw' }}>
          <div style={{ position: 'fixed', top: '0', left: '0', height: '100%', width: sidebarWidth, zIndex: '10', transition: 'width 0.3s ease', background: 'transparent' }}>
            <AdminSidebar />
          </div>
          <div style={{ marginLeft: sidebarWidth, marginTop: '70px', transition: 'margin-left 0.3s ease', minHeight: 'calc(100vh - 70px)', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={mainContentStyle}>
              <div style={cardHeaderContainerStyle}>
                <div style={cardHeaderStyle}>Bulk Upload Students</div>
                <button style={buttonStyle} onClick={handleOpenModal} className="glow-button">
                  Add Student
                </button>
              </div>
              {message && (
                <div style={alertStyle}>
                  <span>{message}</span>
                  <button style={closeButtonStyle} onClick={() => setMessage('')} aria-label="Close">×</button>
                </div>
              )}
              <div style={contentContainerStyle}>
                <div style={{ width: '100%' }}>
                  <label style={labelStyle} htmlFor="csvFile">CSV File</label>
                  <div style={fileInputContainerStyle}>
                    <input type="file" style={fileInputStyle} id="csvFile" accept=".csv" onChange={handleBulkUpload} disabled={loading} className="input-field" />
                    <label htmlFor="csvFile" style={fileInputLabelStyle} className="glow-button">
                      Choose File
                    </label>
                    <div style={fileNameBoxStyle} title={selectedFile}>
                      {selectedFile}
                    </div>
                  </div>
                </div>
                <div style={noteStyle}>
                  CSV should have headers: username,email,password,fullName,admissionNo,collegeName
                </div>
                <button style={buttonStyle} onClick={handleDownloadTemplate} className="glow-button">
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <div style={modalHeaderStyle}>Add New Student</div>
            {message && (
              <div style={alertStyle}>
                <span>{message}</span>
                <button style={closeButtonStyle} onClick={() => setMessage('')} aria-label="Close">×</button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div style={formGridStyle}>
                <div>
                  <label style={labelStyle} htmlFor="username">Username</label>
                  <input type="text" style={inputStyle} id="username" name="username" value={studentData.username} onChange={handleChange} required className="input-field" />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="email">Email</label>
                  <input type="email" style={inputStyle} id="email" name="email" value={studentData.email} onChange={handleChange} required className="input-field" />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="password">Password</label>
                  <input type="password" style={inputStyle} id="password" name="password" value={studentData.password} onChange={handleChange} required className="input-field" />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="fullName">Full Name</label>
                  <input type="text" style={inputStyle} id="fullName" name="fullName" value={studentData.fullName} onChange={handleChange} required className="input-field" />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="admissionNo">Admission No.</label>
                  <input type="text" style={inputStyle} id="admissionNo" name="admissionNo" value={studentData.admissionNo} onChange={handleChange} required className="input-field" />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="collegeName">College Name</label>
                  <input type="text" style={inputStyle} id="collegeName" name="collegeName" value={studentData.collegeName} onChange={handleChange} required className="input-field" />
                </div>
              </div>
              <div style={modalButtonContainerStyle}>
                <button
                  type="button"
                  style={modalCancelButtonStyle}
                  onClick={handleCloseModal}
                  className="modal-cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ ...buttonStyle, opacity: loading ? 0.6 : 1 }}
                  disabled={loading}
                  className="glow-button"
                >
                  {loading ? (
                    <>
                      <span style={{ display: 'inline-block', marginRight: '0.5rem', fontSize: '0.875rem' }}>↻</span>
                      Adding...
                    </>
                  ) : (
                    'Add Student'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AddStudent;